import { SurveyQuestionResponse } from "@yardzen/api-dtos";
import { AddressAutocomplete } from "@yardzen/ui/components/address-autocomplete";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@yardzen/ui/components/toast";
import { useRef, useState } from "react";
import { UseFormRegister } from "react-hook-form";
import AddressConfirmation from "./AddressConfirmation";

type AddressResponseObject = {
  answer: string;
  latitude: number;
  longitude: number;
  placeId: string;
  placeName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

export default function AddressResponse({
  placeholder,
  surveyQuestionResponse,
  register,
}: {
  placeholder: string;
  surveyQuestionResponse?: SurveyQuestionResponse;
  register: UseFormRegister<any>;
}) {
  const response: AddressResponseObject | null =
    surveyQuestionResponse?.response
      ? JSON.parse(surveyQuestionResponse.response)
      : null;

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [place, setPlace] = useState<google.maps.places.PlaceResult | null>(
    null,
  );

  const handleSelectError = (error: string) => {
    setToastOpen(true);
    setToastMessage(error);
  };

  const handleSelectResponse = (
    place: google.maps.places.PlaceResult | null,
  ) => {
    if (place) {
      setPlace(place);
      setDialogOpen(true);
    }
  };

  const handleGoBack = () => {
    setPlace(null);
    setDialogOpen(false);
    if (addressInputRef.current) {
      addressInputRef.current.value = "";
      addressInputRef.current.focus();
    }
  };

  return (
    <div className="mt-4 flex w-full flex-row flex-wrap justify-center px-0 md:mt-0 md:px-8">
      <input
        type="hidden"
        {...register("response")}
        value={
          place
            ? JSON.stringify({
                answer: place.formatted_address,
                latitude: place.geometry?.location?.lat(),
                longitude: place.geometry?.location?.lng(),
                placeId: place.place_id,
                placeName: place.name,
                location: place,
                street:
                  [
                    place.address_components?.find((component) =>
                      component.types.includes("street_number"),
                    )?.long_name,
                    place.address_components?.find((component) =>
                      component.types.includes("route"),
                    )?.long_name,
                  ]
                    .filter(Boolean)
                    .join(" ") || "",
                city:
                  place.address_components?.find((component) =>
                    component.types.includes("locality"),
                  )?.long_name || "",
                state:
                  place.address_components?.find((component) =>
                    component.types.includes("administrative_area_level_1"),
                  )?.short_name || "",
                zip:
                  place.address_components?.find((component) =>
                    component.types.includes("postal_code"),
                  )?.short_name || "",
                country:
                  place.address_components?.find((component) =>
                    component.types.includes("country"),
                  )?.short_name || "",
              })
            : ""
        }
      />
      <AddressAutocomplete
        placeholder={placeholder}
        className="bg-brand-secondary-lighter w-full rounded-md p-4"
        onPlaceSelect={handleSelectResponse}
        onPlaceSelectError={handleSelectError}
        withLocation={true}
        parentInputRef={addressInputRef}
        initialValue={response?.answer ?? ""}
        name="address-autocomplete"
      />
      <AddressConfirmation
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedPlace={place}
        onGoBack={handleGoBack}
      />
      <ToastProvider>
        <Toast open={toastOpen} onOpenChange={setToastOpen}>
          <ToastTitle>Location Error</ToastTitle>
          <ToastDescription>{toastMessage}</ToastDescription>
          <ToastClose />
        </Toast>
        <ToastViewport />
      </ToastProvider>
    </div>
  );
}
